import {
  CloseButton,
  Flex,
  Text,
  BoxProps,
  VStack,
  useColorModeValue,
  IconButton,
  Tooltip,
} from '@chakra-ui/react'
import {
  FiHome,
  FiUsers,
  FiUserCheck,
  FiShoppingBag,
  FiSettings,
  FiActivity,
  FiFileText,
  FiMenu,
  FiX,
  FiPackage,
  FiDollarSign,
  FiLifeBuoy,
  FiMessageSquare,
  FiBell,
  FiBookOpen,
  FiUser,
} from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { NavItem } from './NavItem'

interface SidebarProps extends BoxProps {
  onClose: () => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  showToggle?: boolean
  transitionPhase?: SidebarTransitionPhase
}

export type SidebarTransitionPhase = 'expanded' | 'collapsing' | 'collapsed' | 'expanding'

export function Sidebar({
  onClose,
  isCollapsed = false,
  onToggleCollapse,
  showToggle = false,
  transitionPhase = isCollapsed ? 'collapsed' : 'expanded',
  ...rest
}: SidebarProps) {
  const { t } = useTranslation()
  const bg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')
  const scrollbarThumb = useColorModeValue('gray.300', 'gray.600')
  const labelsVisible = transitionPhase === 'expanded'

  const LinkItems = [
    { name: t('nav.dashboard'), icon: FiHome, path: '/dashboard' },
    { name: t('nav.adminUsers'), icon: FiUserCheck, path: '/admin-users' },
    { name: t('nav.users'), icon: FiUsers, path: '/users' },
    { name: t('nav.merchants'), icon: FiShoppingBag, path: '/merchants' },
    { name: t('nav.orders'), icon: FiFileText, path: '/orders' },
    { name: t('nav.paymentManagement'), icon: FiDollarSign, path: '/payment-management' },
    { name: t('nav.contractManagement'), icon: FiPackage, path: '/contract-management' },
    { name: t('nav.rescue'), icon: FiLifeBuoy, path: '/rescue' },
    { name: t('nav.monitoring'), icon: FiActivity, path: '/monitoring' },
    { name: t('nav.announcements'), icon: FiBell, path: '/announcements' },
    { name: t('nav.documentationContent'), icon: FiBookOpen, path: '/documentation-content' },
    { name: t('nav.customerSupport'), icon: FiMessageSquare, path: '/support' },
    { name: t('nav.systemConfig'), icon: FiSettings, path: '/system' },
    { name: t('nav.auditLogs'), icon: FiFileText, path: '/audit' },
  ]

  const sidebarWidth = isCollapsed ? '16' : '60'

  return (
    <Flex
      direction="column"
      transition="width 0.24s ease"
      bg={bg}
      borderRight="1px"
      borderRightColor={borderColor}
      w={{ base: 'full', md: sidebarWidth }}
      pos="fixed"
      h="full"
      overflow="hidden"
      zIndex={10}
      {...rest}
    >
      {/* Header section */}
      <Flex 
        h="14" 
        flexShrink={0}
        alignItems="center" 
        display="grid"
        gridTemplateColumns={isCollapsed ? '1fr' : '34px minmax(0, 1fr) 34px'}
        gap={2}
        px={isCollapsed ? 2 : 8}
      >
        <Text
          gridColumn={isCollapsed ? 1 : 2}
          fontSize="xs"
          fontWeight="bold"
          letterSpacing="wider"
          textTransform="uppercase"
          color="gray.500"
          noOfLines={1}
          textAlign="center"
          opacity={labelsVisible ? 1 : 0}
          visibility={labelsVisible ? 'visible' : 'hidden'}
          transition="opacity 0.16s ease-in-out"
        >
          {t('sidebar.menu')}
        </Text>

        {/* Desktop collapse/expand button */}
        {showToggle && onToggleCollapse && (
          <Tooltip 
            label={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')} 
            placement="right"
            hasArrow
          >
            <IconButton
              aria-label={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
              icon={isCollapsed ? <FiMenu /> : <FiX />}
              size="sm"
              variant="ghost"
              onClick={onToggleCollapse}
              aria-expanded={!isCollapsed}
              _hover={{ bg: hoverBg }}
              gridColumn={isCollapsed ? 1 : 3}
            />
          </Tooltip>
        )}

        {/* Mobile close button */}
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      
      {/* Navigation items */}
      <VStack
        align="stretch"
        spacing={0.5}
        px={isCollapsed ? 2 : 3}
        flex={1}
        minH={0}
        overflowX="hidden"
        overflowY="auto"
        sx={{
          scrollbarWidth: 'thin',
          scrollbarColor: `${scrollbarThumb} transparent`,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: scrollbarThumb,
            borderRadius: '999px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
        }}
      >
        {LinkItems.map((link) => (
          <NavItem 
            key={link.name} 
            icon={link.icon} 
            path={link.path} 
            onClick={onClose}
            isCollapsed={isCollapsed}
            transitionPhase={transitionPhase}
          >
            {link.name}
          </NavItem>
        ))}
      </VStack>

      <VStack
        align="stretch"
        spacing={0.5}
        px={isCollapsed ? 2 : 3}
        py="3"
        borderTopWidth="1px"
        borderTopColor={borderColor}
        flexShrink={0}
      >
        <NavItem
          icon={FiUser}
          path="/account?tab=mfa"
          onClick={onClose}
          isCollapsed={isCollapsed}
          transitionPhase={transitionPhase}
        >
          {t('nav.account')}
        </NavItem>
      </VStack>
    </Flex>
  )
}
